import React, { useState, useRef, useEffect } from "react";
import { detectType, resolveMediaUrl, getFilenameFromUrl, downloadFile } from "./utils";
import { MdOutlineKeyboardArrowDown, MdDelete, MdEdit, MdCancel } from "react-icons/md";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatMessageTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < DAY_IN_MS) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  const options = {
    day: "2-digit",
    month: "short",
  };

  if (now.getFullYear() !== date.getFullYear()) {
    options.year = "numeric";
  }

  return date.toLocaleDateString([], options);
};

const formatFullDateTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const MessageItem = ({ msg, index, isCurrentUser, showAvatar, currentUserId, openViewer, videoPlayingRef, onEditMessage, onDeleteMessage, isEditing, onSaveEdit, onCancelEdit, editText, onEditTextChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const bubbleRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);

  // Ensure we can always render the current user's avatar even if sender.profileimg is missing in a real-time edit
  let currentUserAvatar = null;
  try {
    const authRaw = typeof window !== 'undefined' ? localStorage.getItem('authUser') : null;
    if (authRaw) {
      const auth = JSON.parse(authRaw);
      currentUserAvatar = auth?.user?.profileimg || auth?.profileimg || null;
    }
  } catch { /* noop */ }

  // Treat server variations consistently as "deleted for everyone"
  const isEffectivelyDeletedForEveryone =
    msg.isDeleteEvery === true ||
    msg.deletedFor === 'everyone' ||
    (msg.isDeleted === true && msg.isDeleteMe !== true) ||
    (typeof msg.content === 'string' && /this message has been deleted/i.test(msg.content));

  const canShowDropdown = !isEffectivelyDeletedForEveryone && !msg.isDeleteMe && !isEditing;
  const isDeletedForEveryone = isEffectivelyDeletedForEveryone;

  const t = isDeletedForEveryone ? 'text' : (msg.messageType || detectType('', msg.mediaUrl || msg.content || ''));
  const src = isDeletedForEveryone ? null : (msg.mediaUrl || (typeof msg.content === 'string' && /\/uploads\//.test(msg.content) ? msg.content : ''));

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [isEditing]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!canShowDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [canShowDropdown]);

  const handleEdit = () => {
    setShowDropdown(false);
    if (onEditMessage) {
      onEditMessage(msg);
    }
  };

  const handleDelete = () => {
    setShowDropdown(false);
    if (onDeleteMessage) {
      onDeleteMessage(msg);
    }
  };

  const toggleDropdown = (e) => {
    if (!canShowDropdown) return;
    e.stopPropagation();
    // Decide whether to open upwards (to avoid being hidden behind input at bottom)
    try {
      const bubble = bubbleRef.current;
      if (bubble) {
        const rect = bubble.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const estimatedMenuHeight = 90; // px ~ two items
        setDropUp(spaceBelow < estimatedMenuHeight + 16);
      }
    } catch { }
    setShowDropdown((prev) => !prev);
  };

  const handleSave = () => {
    if (onSaveEdit && editText.trim()) {
      onSaveEdit(msg._id, editText.trim());
    }
  };

  const handleCancel = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const renderMessageContent = () => {

    // Special rendering for messages deleted for everyone
    if (isDeletedForEveryone) {
      const text = typeof msg.content === "string" && msg.content.trim() !== ""
        ? msg.content
        : "This message has been deleted";
      return (
        <div className="d-flex align-items-center" style={{ gap: 8, opacity: 0.9 }}>
          <MdCancel size={16} />
          <span>{text}</span>
        </div>
      );
    }

    if (t === 'image' && (src || msg.content)) {
      const imageSrc = resolveMediaUrl(src || msg.content);
      const name = msg.fileName || getFilenameFromUrl(imageSrc);
      const imageKey = `image-${msg._id || index}-${imageSrc}`;
      return (
        <div key={imageKey} className="media-wrap" style={{ position: 'relative', display: 'inline-block' }}>
          <img
            key={imageKey}
            onClick={() => openViewer(imageSrc, 'image')}
            src={imageSrc}
            alt="attachment"
            loading="lazy"
            style={{ cursor: 'zoom-in', display: 'block', maxWidth: 300, width: '100%', height: 'auto', borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }}
          />
          <button className="download-btn btn btn-sm btn-light" onClick={() => downloadFile(imageSrc, name)} style={{ position: 'absolute', top: 6, right: 6, opacity: 0, transition: 'opacity 0.2s' }}>
            <i className="ri-download-2-line"></i>
          </button>
          <style>{`.media-wrap:hover .download-btn{opacity:1}`}</style>
        </div>
      );
    }
    if (t === 'video' && (src || msg.content)) {
      const videoSrc = resolveMediaUrl(src || msg.content);
      const name = msg.fileName || getFilenameFromUrl(videoSrc);
      const videoKey = `video-${msg._id || index}-${videoSrc}`;
      return (
        <div key={videoKey} className="media-wrap" style={{ position: 'relative', display: 'inline-block' }}>
          <video
            key={videoKey}
            onClick={(e) => {
              e.stopPropagation();
              openViewer(videoSrc, 'video');
            }}
            controls
            src={videoSrc}
            preload="none"
            playsInline
            onPlay={(e) => {
              videoPlayingRef.current.add(videoKey);
            }}
            onPause={(e) => {
              videoPlayingRef.current.delete(videoKey);
            }}
            onEnded={(e) => {
              videoPlayingRef.current.delete(videoKey);
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              const errorDiv = e.target.parentElement.querySelector('.video-error');
              if (errorDiv) errorDiv.style.display = 'block';
              videoPlayingRef.current.delete(videoKey);
            }}
            onLoadStart={(e) => {
              e.stopPropagation();
            }}
            style={{ cursor: 'zoom-in', display: 'block', maxWidth: 300, width: '100%', borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }}
          />
          <div className="video-error" style={{ display: 'none', padding: '10px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
            <i className="ri-video-line" style={{ fontSize: '24px', color: '#6c757d' }}></i>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>Video unavailable</p>
          </div>
          <button className="download-btn btn btn-sm btn-light" onClick={(e) => {
            e.stopPropagation();
            downloadFile(videoSrc, name);
          }} style={{ position: 'absolute', top: 6, right: 6, opacity: 0, transition: 'opacity 0.2s' }}>
            <i className="ri-download-2-line"></i>
          </button>
          <style>{`.media-wrap:hover .download-btn{opacity:1}`}</style>
        </div>
      );
    }
    if (t === 'audio' && (src || msg.content)) {
      const audioSrc = resolveMediaUrl(src || msg.content);
      const name = msg.fileName || getFilenameFromUrl(audioSrc);
      const audioKey = `audio-${msg._id || index}-${audioSrc}`;
      return (
        <div key={audioKey} style={{ width: 300 }}>
          <audio
            key={audioKey}
            controls
            src={audioSrc}
            preload="none"
            onPlay={(e) => {
              e.stopPropagation();
              videoPlayingRef.current.add(audioKey);
            }}
            onPause={(e) => {
              e.stopPropagation();
              videoPlayingRef.current.delete(audioKey);
            }}
            onEnded={(e) => {
              e.stopPropagation();
              videoPlayingRef.current.delete(audioKey);
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              videoPlayingRef.current.delete(audioKey);
            }}
            onLoadStart={(e) => {
              e.stopPropagation();
            }}
            style={{ width: '100%' }}
          />
        </div>
      );
    }
    if (t === 'pdf' && (src || msg.content)) {
      const href = resolveMediaUrl(src || msg.content);
      const name = msg.fileName || getFilenameFromUrl(href);
      return (
        <div style={{ maxWidth: 300, border: '1px solid #e5e7eb', borderRadius: 8, background: isCurrentUser ? 'rgba(255,255,255,0.1)' : '#fff' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ri-file-pdf-line" style={{ fontSize: 18 }}></i>
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            </div>
          </div>
          <div style={{ padding: 10 }}>
            <button onClick={() => openViewer(href, 'pdf')} className="btn btn-sm btn-light w-100 mb-2">View</button>
            <button onClick={() => downloadFile(href, name)} className="btn btn-sm btn-primary w-100">Download</button>
          </div>
        </div>
      );
    }

    return <div style={{ fontSize: "0.95rem" }}>{msg.content}</div>;
  };

  return (
    <div
      key={msg._id || index}
      className={`d-flex mb-2 align-items-end ${isCurrentUser ? "justify-content-end" : "justify-content-start"}`}
      style={{ paddingLeft: isCurrentUser ? "15%" : "0", paddingRight: !isCurrentUser ? "15%" : "0" }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          className={`d-flex align-items-center position-relative ${canShowDropdown && !isDeletedForEveryone ? "message-hover" : ""}`}
          style={{ gap: "4px" }}
          ref={bubbleRef}
        >
          {isCurrentUser && canShowDropdown && !isDeletedForEveryone && (
            <div
              ref={dropdownRef}
              className="position-relative"
            >
              <div
                className="msg-arrow"
                onClick={toggleDropdown}
                style={{
                  opacity: showDropdown ? 1 : 0,
                  transition: "opacity 0.2s",
                  cursor: "pointer",
                  backgroundColor: "#00209f",
                  borderRadius: "50%",
                  padding: "4px",
                  boxShadow: "rgb(0 0 0 / 50%) 0px 1px 2px",
                  position: 'relative'
                }}
              >
                <MdOutlineKeyboardArrowDown size={18} color="white" />

                {showDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      ...(dropUp ? { bottom: '100%' } : { top: '100%' }),
                      right: 0,
                      marginTop: '8px',
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 3000,
                      minWidth: 120
                    }}
                  >
                    {(() => {
                      // Calculate time difference between message creation and now
                      const createdAt = new Date(msg.createdAt);
                      const now = new Date();
                      const hoursSince = (now - createdAt) / (1000 * 60 * 60);

                      // Allow editing only within 12 hours (same as backend)
                      const canEditNow = hoursSince <= 12;

                      // Conditions: within edit window, not deleted, and canEdit not explicitly false
                      return canEditNow && msg?.canEdit !== false && !isDeletedForEveryone ? (
                        <button
                          onClick={handleEdit}
                          className="btn btn-sm w-100 text-start d-flex align-items-center"
                          style={{ padding: '8px 12px', border: 'none', borderBottom: '1px solid #f0f0f0' }}
                          disabled={msg?.canEdit === false}
                        >
                          <MdEdit size={16} className="me-2" />
                          Edit
                        </button>
                      ) : null;
                    })()}

                    <button
                      onClick={handleDelete}
                      className="btn btn-sm w-100 text-start d-flex align-items-center"
                      style={{ padding: '8px 12px', border: 'none' }}
                    >
                      <MdDelete size={16} className="me-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isCurrentUser && (
            showAvatar ? (
              <div className="d-flex align-items-center">
                {msg.sender?.profileimg && msg.sender.profileimg.trim() !== "" ? (
                  <>
                    <img
                      src={resolveMediaUrl(msg.sender.profileimg)}
                      alt={`${msg.sender?.firstname || ''} ${msg.sender?.lastname || ''}`.trim() || "User"}
                      className="rounded-circle me-2"
                      style={{
                        width: 32,
                        height: 32,
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.parentElement.querySelector('.sender-avatar-fallback');
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  </>
                ) : (
                  <div
                    className="avatar-xs rounded-circle bg-primary-subtle text-primary fw-bold d-flex align-items-center justify-content-center me-2 sender-avatar-fallback"
                    style={{ width: 32, height: 32, flexShrink: 0 }}
                  >
                    {(() => {
                      const first = msg.sender?.firstname?.[0] || msg.sender?.name?.split(" ")[0]?.[0] || "";
                      const last = msg.sender?.lastname?.[0] || msg.sender?.name?.split(" ")[1]?.[0] || "";
                      return (first + last || "U").toUpperCase();
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{ width: 32, height: 32, marginRight: "0.5rem", flexShrink: 0 }}
              ></div>
            )
          )}

          <div
            className={`${isCurrentUser ? "bg-primary text-white" : "bg-white text-dark border"}`}
            style={{
              maxWidth: "100%",
              wordBreak: "break-word",
              borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              padding: (msg.messageType === 'image' || msg.messageType === 'video') ? 10 : 12,
              cursor: isDeletedForEveryone || msg.isDeleteMe ? 'default' : 'auto'
            }}
          >
            {renderMessageContent()}
          </div>

          {!isCurrentUser && canShowDropdown && !isDeletedForEveryone && (
            <div
              ref={dropdownRef}
              className="position-relative"
            >
              <div
                className="msg-arrow"
                onClick={toggleDropdown}
                style={{
                  opacity: showDropdown ? 1 : 0,
                  transition: "opacity 0.2s",
                  cursor: "pointer",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  padding: "4px",
                  boxShadow: "rgb(0 0 0 / 50%) 0px 1px 2px",
                  position: 'relative'
                }}
              >
                <MdOutlineKeyboardArrowDown size={18} />

                {showDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '8px',
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      minWidth: 120
                    }}
                  >
                    <button
                      onClick={handleDelete}
                      className="btn btn-sm w-100 text-start d-flex align-items-center"
                      style={{ padding: '8px 12px', border: 'none' }}
                    >
                      <MdDelete size={16} className="me-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <style>{`
            ${canShowDropdown && !isDeletedForEveryone ? `
              .message-hover:hover .msg-arrow {
                opacity: 1 !important;
              }
            ` : ''}
            
            .msg-arrow[style*="opacity: 1"] {
              opacity: 1 !important;
            }

            .message-hover:not(.deleted-message):hover .msg-arrow {
              opacity: 1 !important;
            }
          `}</style>
        </div>

        <div
          className={`mt-1 d-flex align-items-center ${isCurrentUser ? "justify-content-end" : "justify-content-start"}`}
          style={{
            fontSize: "0.7rem",
            opacity: 0.8,
            gap: "4px",
            marginLeft: !isCurrentUser ? "41px" : "0px",
          }}
        >
          <span>{formatMessageTime(msg.createdAt)}</span>
          {(msg.isEdited === true || msg.editedAt) && !isDeletedForEveryone && (
            <>
              <span style={{ padding: "0 4px" }}>•</span>
              <span
                style={{ fontStyle: "italic" }}
                title={msg.editedAt ? `Edited at ${formatFullDateTime(msg.editedAt)}` : "Edited"}
              >
                edited
              </span>
            </>
          )}


          {isCurrentUser && (
            <span style={{ fontSize: "0.6rem" }}>
              {msg.status === "failed" ? "❌" : "✓"}
            </span>
          )}
        </div>
      </div>
      {isCurrentUser && (
        showAvatar ? (
          (() => {
            // Prefer message sender's profile image; fall back to authenticated user's avatar if absent
            const profileUrl = (msg.sender?.profileimg && msg.sender.profileimg.trim() !== "")
              ? msg.sender.profileimg
              : (currentUserAvatar || "");
            const hasProfile = profileUrl && String(profileUrl).trim() !== "";
            return hasProfile ? (
              <img
                src={resolveMediaUrl(profileUrl)}
                alt={`${msg.sender?.firstname || ''} ${msg.sender?.lastname || ''}`.trim() || "User"}
                className="rounded-circle ms-2"
                style={{
                  width: 32,
                  height: 32,
                  objectFit: "cover",
                  flexShrink: 0
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  const fallback = e.target.parentElement.querySelector('.current-user-avatar-fallback');
                  if (fallback) fallback.style.display = "flex";
                }}
                onLoad={(e) => {
                  const fallback = e.target.parentElement.querySelector('.current-user-avatar-fallback');
                  if (fallback) fallback.style.display = "none";
                }}
              />
            ) : null;
          })() || (
            <div
              className="avatar-xs rounded-circle bg-primary text-white fw-bold d-flex align-items-center justify-content-center ms-2 current-user-avatar-fallback"
              style={{ width: 32, height: 32, flexShrink: 0 }}
            >
              {(() => {
                const first = msg.sender?.firstname?.[0] || msg.sender?.name?.split(" ")[0]?.[0] || "";
                const last = msg.sender?.lastname?.[0] || msg.sender?.name?.split(" ")[1]?.[0] || "";
                return (first + last || "U").toUpperCase();
              })()}
            </div>
          )
        ) : (
          <div
            style={{ width: 32, height: 32, marginLeft: "0.5rem", flexShrink: 0 }}
          ></div>
        )
      )}
    </div>
  );
};

export default MessageItem;